import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { fetchLEUserSubscriptionsV2 } from 'api/LESubscriptions'
import { fetchAlertPreferences } from 'api/savedAlerts'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getPrefixCountry } from 'lib/geo/getPrefixCountry'
import type { ContentSubscriptions } from 'components/Common/Alerts/alertContentSubscriptions'
import { ASSUMED_CONTENT_SUBSCRIPTIONS } from 'components/Common/Alerts/alertContentSubscriptions'

export interface AlertChannelChoiceValues {
  email: boolean
  sms: boolean
  newPhoneNumber?: { phone: string, prefix: string }
  subscribeToTodaysEscapes?: boolean
  contentSubscriptions: ContentSubscriptions
}

export interface AlertChannelSelection {
  loaded: boolean
  channelsLoaded: boolean
  email: boolean
  sms: boolean
  setEmail: (checked: boolean) => void
  setSms: (checked: boolean) => void
  hasPhoneNumber: boolean
  needsPhoneNumber: boolean
  phoneMissing: boolean
  phoneNumber: string
  phonePrefix: string
  phonePrefixCountry: string
  setPhoneNumber: (phone: string) => void
  setPhonePrefix: (prefix: string, country: string) => void
  showMarketingOptIn: boolean
  subscribeToTodaysEscapes: boolean
  setSubscribeToTodaysEscapes: (checked: boolean) => void
  choice: AlertChannelChoiceValues
}

export default function useAlertChannelSelection(): AlertChannelSelection {
  const userId = useAppSelector(state => state.auth.account.memberId)
  const hasPhoneNumber = useAppSelector(state => !!state.auth.account.phone)
  const { currentRegionCode } = useContext(GeoContext)

  const [loaded, setLoaded] = useState<boolean>(false)
  const [channelsLoaded, setChannelsLoaded] = useState<boolean>(false)
  const [isEmailSubscribed, setIsEmailSubscribed] = useState<boolean>(true)
  const [email, setEmail] = useState<boolean>(true)
  const [sms, setSms] = useState<boolean>(false)
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [prefix, setPrefix] = useState<{ phonePrefix: string, prefixCountry: string }>(
    () => getPrefixCountry(currentRegionCode),
  )
  const [subscribeToTodaysEscapes, setSubscribeToTodaysEscapes] = useState<boolean>(true)
  const [contentSubscriptions, setContentSubscriptions] = useState<ContentSubscriptions>(ASSUMED_CONTENT_SUBSCRIPTIONS)

  useEffect(() => {
    if (!userId) {
      setLoaded(true)
      return
    }
    let cancelled = false
    Promise.allSettled([
      fetchLEUserSubscriptionsV2(userId),
      fetchAlertPreferences(userId),
    ]).then(([subscriptions, channels]) => {
      if (cancelled) return
      if (subscriptions.status === 'fulfilled') {
        const { emailSubscribed, todaysEscapesSubscribed, curatedCollectionSubscribed, myJourneySubscribed } = subscriptions.value.settings
        setIsEmailSubscribed(emailSubscribed)
        setContentSubscriptions({ todaysEscapesSubscribed, curatedCollectionSubscribed, myJourneySubscribed })
      }
      if (channels.status === 'fulfilled') {
        setEmail(channels.value.emailEnabled)
        setSms(channels.value.smsEnabled && hasPhoneNumber)
        setChannelsLoaded(true)
      }
      setLoaded(true)
    })
    return () => { cancelled = true }
  }, [userId, hasPhoneNumber])

  const showMarketingOptIn = loaded && !isEmailSubscribed
  const needsPhoneNumber = sms && !hasPhoneNumber
  const phoneMissing = needsPhoneNumber && !phoneNumber

  const choice = useMemo<AlertChannelChoiceValues>(() => ({
    email,
    sms,
    newPhoneNumber: needsPhoneNumber && phoneNumber ?
        { phone: phoneNumber, prefix: prefix.phonePrefix } :
      undefined,
    subscribeToTodaysEscapes: showMarketingOptIn ? subscribeToTodaysEscapes : undefined,
    contentSubscriptions,
  }), [email, sms, needsPhoneNumber, phoneNumber, prefix.phonePrefix, showMarketingOptIn, subscribeToTodaysEscapes, contentSubscriptions])

  const setPhonePrefix = useCallback((phonePrefix: string, prefixCountry: string) => {
    setPrefix({ phonePrefix, prefixCountry })
  }, [])

  return {
    loaded,
    channelsLoaded,
    email,
    sms,
    setEmail,
    setSms,
    hasPhoneNumber,
    needsPhoneNumber,
    phoneMissing,
    phoneNumber,
    phonePrefix: prefix.phonePrefix,
    phonePrefixCountry: prefix.prefixCountry,
    setPhoneNumber,
    setPhonePrefix,
    showMarketingOptIn,
    subscribeToTodaysEscapes,
    setSubscribeToTodaysEscapes,
    choice,
  }
}
